<template>
    <div :class="withBadges ? $style.container : ''">
        <CardContainer
            :is="props.game ? BaseLink : 'div'"
            :appearance="props.appearance"
            :class="[$style.gameCard_override, classForGame ? classForGame : '', shouldPreloadVideo && 'lazyload']"
            :alt="props.game?.title"
            :src="props.game?.preview"
            :responsive="props.responsive"
            v-bind="linkData"
            :lazy="props.lazy"
            :preload="props.preload"
            :appearanceMedia="props.appearanceMedia"
            :approximateWidth="props.approximateWidth"
            :sources="props.sources"
            :placeholder="placeholder"
            itemscope
            itemtype="https://schema.org/VideoGame"
            itemprop="url"
            data-interface-target="game"
            :data-video-id="shouldPreloadVideo ? props.game?.videoId : undefined"
            @mouseenter="showVideo"
            @mouseleave="closeVideo"
            @focus="showVideo"
            @blur="closeVideo"
        >
            <GameVideo
                ref="videoElemRef"
                :videoId="props.game?.videoId"
                :videoUrl="props.game?.videoUrl"
            />
            <Typography
                v-if="props.game"
                size="xs"
                type="label"
                accent
                :responsive="false"
                itemprop="name"
                :class="$style.title"
            >
                {{ props.game.title }}
            </Typography>
            <div
                v-if="props.game?.debugInfo && user.isCompanyEmployeeLoggedIn"
                :class="$style.debug_icon"
                @click.prevent.stop="showDebugInfo"
            >
                ⓘ
            </div>
        </CardContainer>
        <Badge
            v-if="withBadges"
            :badges="props.game?.badges"
            :isHide="isMouseOver"
        />
    </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'

import Badge from '@/components_new/Badge/Badge.vue'
import BaseLink from '@/components_new/BaseLink.vue'
import CardContainer, { type AppearanceMediaCondition, CardAppearance } from '@/components_new/CardContainer.vue'
import GameVideo from '@/components_new/GameVideo.vue'
import { isPWARuntime } from '@/components_new/PWAInstall/utils'
import Typography from '@/components_new/Typography.vue'
import { useGetExternalUrl } from '@/composables/url-helpers'
import { ROUTE_NAMES } from '@/router/constants'
import { useAppStore } from '@/store/app-store'
import { useDebugDialogStore } from '@/store/debug-dialog-store'
import { usePreloadPlayerStore } from '@/store/preload-player-store'
import { useUserStore } from '@/store/user-store/user-store'
import type { LinkTarget, ShortGameType } from '@/types'
import { useIsMounted } from '@/utils/vue-hooks/use-is-mounted'

type VueClassBinding = string | Record<string, unknown> | (string | Record<string, unknown>)[]

interface GameCardProps {
    game?: ShortGameType
    appearance?: CardAppearance
    responsive?: boolean
    placeholder?: boolean
    targetLink?: LinkTarget
    appearanceMedia?: AppearanceMediaCondition[]
    approximateWidth?: number
    sources?: string[]
    lazy?: boolean
    preload?: boolean
    smallBadge?: boolean
    classForGame?: VueClassBinding
    withBadges?: boolean
}

const props = withDefaults(defineProps<GameCardProps>(), {
    lazy: true,
})

const user = useUserStore()
const appStore = useAppStore()
const debugStore = useDebugDialogStore()
const { preloadPlayer } = usePreloadPlayerStore()

const { platform } = storeToRefs(appStore)

const isMounted = useIsMounted()
const shouldPreloadVideo = computed(() => !!(props.game?.videoId && isMounted.value && platform.value === 'desktop'))

const getExternalUrl = useGetExternalUrl()

const linkData = computed(() => {
    const to = props.game && { name: ROUTE_NAMES.GAME_PAGE, params: { game: props.game.hru } }
    return isPWARuntime() ? { to } : { externalTo: to && getExternalUrl(to), target: props.targetLink }
})

const videoElemRef = ref<InstanceType<typeof GameVideo>>()
const isMouseOver = ref(false)

function showVideo() {
    videoElemRef.value?.showVideo()
    isMouseOver.value = true
}

function closeVideo() {
    videoElemRef.value?.closeVideo()
    isMouseOver.value = false
}

function showDebugInfo() {
    debugStore.setDebugData({
        title: props.game?.title || '',
        data: props.game?.debugInfo,
    })
}

onMounted(() => {
    if (props.game?.videoId) {
        preloadPlayer()
    }
})
</script>

<style module>
.container {
    margin: 3px -3px 3px 3px;
    position: relative;
}

.title {
    display: none;
    position: relative;
}

.gameCard_override:has(video) img {
    animation-duration: 1s;
    animation-iteration-count: infinite;
    animation-direction: alternate;
    animation-name: video-loading-pulse;
    animation-timing-function: ease-out;
    animation-fill-mode: both;
}

@keyframes video-loading-pulse {
    from {
        transform: scale(1);
    }
    to {
        transform: scale(1.05);
    }
}

.gameCard_override:hover .title,
.gameCard_override:focus-visible .title {
    display: block;
    grid-area: main;
    padding: 8px 12px 8px;
    align-self: end;
    background: linear-gradient(0deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0) 100%);
}

.debug_icon {
    position: absolute;
    bottom: 4px;
    right: 4px;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    font-size: 16px;
    line-height: 25px;
    text-align: center;
    background: var(--white);
    color: var(--carbon-400);
    transition: all 0.3s ease-in-out;
}

@media (--desktop-small-plus) {
    .debug_icon {
        opacity: 0;
    }

    .gameCard_override:hover .debug_icon {
        opacity: 1;
    }

    .debug_icon:hover {
        background: var(--white-80);
        color: var(--carbon-500);
    }
}
</style>
